import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS_AHEAD_FOR_PREPAID = 6;
const COMMISSION_MONTHS_PER_PAGE = 12;
const TT_OFFSET_MS = 4 * 60 * 60 * 1000;

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: "1.25rem",
  },
  cardHead: {
    padding: "0.75rem 1.125rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1d23",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardBody: { padding: "1rem 1.125rem" },
  kpiCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1rem 1.125rem",
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 6,
  },
  kpiValue: { fontSize: 22, fontWeight: 700, lineHeight: 1, color: "#1a1d23" },
  kpiSub: { fontSize: 12, color: "#9ca3af", marginTop: 5 },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 2,
  },
  fieldValue: { fontSize: 13, color: "#1a1d23" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
  },
  pill: (variant) => {
    const map = {
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
      red: { background: "#FEF2F2", color: "#991B1B" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  btn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnGreen: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 6,
    border: "1px solid #BBF7D0",
    background: "transparent",
    color: "#166534",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 14px",
    borderRadius: 6,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
  },
  btnLink: {
    fontSize: 11,
    color: "#6b7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline",
  },
  th: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#6b7280",
    padding: "8px 12px",
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    background: "#fafafa",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1a1d23",
    fontSize: 13,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  spinner: {
    display: "inline-block",
    width: 12,
    height: 12,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 5,
  },
  input: {
    fontSize: 13,
    padding: "5px 9px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    color: "#1a1d23",
    background: "#fff",
    width: 80,
    outline: "none",
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatCurrency = (n) =>
  new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);

const toTrinidadAdjustedDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getTime() + TT_OFFSET_MS);
};

const getTrinidadDateParts = (value) => {
  const adjusted = toTrinidadAdjustedDate(value);
  if (!adjusted) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(adjusted);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value || 0);
  return { year: get("year"), month: get("month"), day: get("day") };
};

const formatDate = (date) => {
  const adjusted = toTrinidadAdjustedDate(date);
  if (!adjusted) return "Not set";
  return adjusted.toLocaleDateString("en-US", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatMonthDayYearTT = (year, month, day) => {
  const d = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return d.toLocaleDateString("en-US", {
    timeZone: "America/Port_of_Spain",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const monthName = (m) => new Date(2000, m - 1, 1).toLocaleString("en-US", { month: "short" });

const addOneMonthParts = (parts) => {
  if (!parts) return null;
  if (parts.month === 12) return { year: parts.year + 1, month: 1, day: parts.day };
  return { ...parts, month: parts.month + 1 };
};

const addMonthsToParts = (parts, delta) => {
  if (!parts) return null;
  let m = parts.month + delta;
  let y = parts.year;
  while (m > 12) {
    m -= 12;
    y += 1;
  }
  while (m < 1) {
    m += 12;
    y -= 1;
  }
  return { year: y, month: m, day: parts.day };
};

const minMonthYear = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  if (a.year < b.year) return a;
  if (a.year > b.year) return b;
  return a.month <= b.month ? a : b;
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Spinner = () => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <span style={s.spinner} />
  </>
);

const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const FieldRow = ({ label, value }) => (
  <div>
    <div style={s.fieldLabel}>{label}</div>
    <div style={s.fieldValue}>{value ?? "—"}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "6px 0",
      borderBottom: "1px solid #f3f4f6",
      fontSize: 13,
    }}
  >
    <span style={{ color: "#6b7280" }}>{label}</span>
    <span style={{ fontWeight: 500 }}>{value}</span>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const PropertyManagementFinancials = ({ property, onPropertyUpdated }) => {
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [commissionPage, setCommissionPage] = useState(1);
  const [editingRate, setEditingRate] = useState(false);
  const [rateInput, setRateInput] = useState("");
  const [savingRate, setSavingRate] = useState(false);
  const [payModal, setPayModal] = useState({ open: false, month: null, year: null, amountDue: 0, amountAlreadyPaid: 0 });
  const [payInput, setPayInput] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [payDate, setPayDate] = useState("");
  const [submittingPay, setSubmittingPay] = useState(false);
  const [payError, setPayError] = useState("");
  const { getAuthHeaders } = useAuth();

  const safeProperty = property || {};

  // ── Fetch payments ────────────────────────────────────────────────────────
  const fetchPayments = useCallback(async () => {
    if (!safeProperty._id) return;
    setLoadingPayments(true);
    try {
      const res = await fetch(API_ENDPOINTS.payments.getByProperty(safeProperty._id), {
        headers: { "ngrok-skip-browser-warning": "true", ...getAuthHeaders() },
      });
      const data = await res.json();
      if (res.ok) setPayments(data.data || []);
    } catch (err) {
      console.error("Failed to fetch payments", err);
    } finally {
      setLoadingPayments(false);
    }
  }, [safeProperty._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // ── Derived financial values ──────────────────────────────────────────────
  const monthlyRent =
    safeProperty.pricing?.rent || safeProperty.managementInfo?.monthlyRentCollected || 0;
  const commission = safeProperty.pricing?.rentalCommission || {};
  const ongoingRate = commission.ongoingRate != null ? commission.ongoingRate : 5;

  const leaseStartRaw =
    safeProperty.managementInfo?.leaseStart ||
    safeProperty.occupancy?.leaseStart ||
    safeProperty.placementInfo?.tenantInfo?.leaseStartDate;
  const leaseEndRaw =
    safeProperty.managementInfo?.leaseEnd ||
    safeProperty.occupancy?.leaseEnd ||
    safeProperty.placementInfo?.tenantInfo?.leaseEndDate;
  const leaseStartParts = getTrinidadDateParts(leaseStartRaw);
  const leaseEndParts = getTrinidadDateParts(leaseEndRaw);
  const rentDueDay = leaseStartParts?.day ?? null;

  const contractRaw = safeProperty.managementInfo?.contractStartDate;
  const isConverted = !!contractRaw;
  const contractStartParts = getTrinidadDateParts(contractRaw);
  const placementPaidBefore =
    safeProperty.managementInfo?.placementCommissionPaidBeforeConversion === true;

  // currentTermStart is set on link and advanced on renewal, so the schedule
  // "starts fresh" from the current lease term. Older records may not have it —
  // fall back to the existing lease/contract-derived start in that case.
  const currentTermStartRaw = safeProperty.managementInfo?.currentTermStart;
  const currentTermStartParts = getTrinidadDateParts(currentTermStartRaw);
  // Whether THIS term charges a placement (first-month) commission. Defaults to
  // true for legacy records with no explicit flag.
  const currentTermChargesPlacement =
    safeProperty.managementInfo?.currentTermChargesPlacement !== false;

  let commissionStartParts;
  if (currentTermStartParts) {
    // Renewed / freshly-linked term: schedule begins at the current term start.
    commissionStartParts = currentTermStartParts;
  } else if (isConverted && contractStartParts) {
    commissionStartParts = placementPaidBefore
      ? addOneMonthParts(contractStartParts)
      : contractStartParts;
  } else {
    const raw = safeProperty.managementInfo?.leaseStart || safeProperty.occupancy?.leaseStart;
    commissionStartParts = getTrinidadDateParts(raw);
  }

  const contractMonthKey = contractStartParts
    ? `${contractStartParts.year}-${contractStartParts.month}`
    : "";
  const scheduleStartParts = commissionStartParts || leaseStartParts;
  const firstScheduleMonthKey = scheduleStartParts
    ? `${scheduleStartParts.year}-${scheduleStartParts.month}`
    : "";

  let placementRowKey;
  if (currentTermStartParts) {
    // Current term controls placement explicitly (checkbox on renewal / initial link).
    placementRowKey = currentTermChargesPlacement ? firstScheduleMonthKey : null;
  } else if (isConverted && placementPaidBefore) {
    placementRowKey = null;
  } else if (isConverted && !placementPaidBefore) {
    placementRowKey = contractMonthKey;
  } else {
    placementRowKey = firstScheduleMonthKey;
  }

  const isPlacementRow = (key) => Boolean(placementRowKey && key === placementRowKey);

  // ── Payment maps ──────────────────────────────────────────────────────────
  const paidMap = useMemo(() => {
    const map = new Map();
    for (const payment of payments) {
      if (payment.type !== "management_fee") continue;
      const key = `${payment.year}-${payment.month}`;
      if (!map.has(key)) map.set(key, payment);
    }
    return map;
  }, [payments]);

  // Did a renewal happen? The current term start is after the property's original
  // (contract/earliest lease) start — so earlier months belong to a previous term
  // and now live in the Commission history section rather than the schedule below.
  const originalStartRaw =
    safeProperty.managementInfo?.contractStartDate ||
    safeProperty.occupancy?.leaseStart ||
    null;
  const originalStartParts = getTrinidadDateParts(originalStartRaw);
  const isRenewedTerm = Boolean(
    currentTermStartParts &&
      originalStartParts &&
      (currentTermStartParts.year > originalStartParts.year ||
        (currentTermStartParts.year === originalStartParts.year &&
          currentTermStartParts.month > originalStartParts.month))
  );

  // Recorded management-fee payments dated before the current term that are still
  // unpaid or only partially paid — money potentially owed from a previous term.
  const unpaidPreviousTermCount = useMemo(() => {
    if (!currentTermStartParts) return 0;
    const cutoff = currentTermStartParts.year * 12 + currentTermStartParts.month;
    let count = 0;
    for (const p of payments) {
      if (p.type !== "management_fee") continue;
      if (!p.month || !p.year) continue;
      const monthIndex = p.year * 12 + p.month;
      if (monthIndex >= cutoff) continue;
      if (p.status === "partial" || (p.status && p.status !== "paid")) count += 1;
    }
    return count;
  }, [payments, currentTermStartParts]);

  const placementPayments = useMemo(
    () => payments.filter((p) => p.type === "rent_placement"),
    [payments]
  );

  // ── Commission history (read-only record of past paid commissions) ──────────
  // Combines the pre-conversion placement commission (if it was collected before
  // this property became a management contract) with any recorded management-fee
  // payments. This is distinct from the forward-looking schedule below.
  const commissionHistory = useMemo(() => {
    const rows = [];

    // Placement commission that was paid before conversion (not part of the
    // ongoing schedule, so it never appears there — surface it here instead).
    const placementCommissionPaid =
      placementPaidBefore &&
      commission.isPaid &&
      (safeProperty.placementInfo?.commissionPaidDate || commission.amount != null);
    if (placementCommissionPaid) {
      rows.push({
        id: "placement-preconversion",
        date: safeProperty.placementInfo?.commissionPaidDate || null,
        type: "Placement (pre-conversion)",
        amount: commission.amount ?? 0,
        paidBy: commission.paidBy || null,
      });
    }

    // Recorded management-fee (ongoing commission) payments.
    for (const p of payments) {
      if (p.type !== "management_fee") continue;
      if (p.status && p.status !== "paid" && p.status !== "partial") continue;
      rows.push({
        id: p._id || `mf-${p.year}-${p.month}`,
        date: p.paidAt || null,
        type:
          p.month && p.year
            ? `Ongoing · ${monthName(p.month)} ${p.year}`
            : "Ongoing commission",
        amount: p.amount ?? 0,
        paidBy: p.tenant
          ? `${p.tenant.personalInfo?.firstName || ""} ${p.tenant.personalInfo?.lastName || ""}`.trim() ||
            "Tenant"
          : "Landlord",
        partial: p.status === "partial",
        tenant: p.tenant || null,
      });
    }

    // Newest first.
    rows.sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return tb - ta;
    });
    return rows;
  }, [payments, placementPaidBefore, commission, safeProperty.placementInfo]);

  const commissionHistoryTotal = useMemo(
    () => commissionHistory.reduce((sum, r) => sum + (r.amount || 0), 0),
    [commissionHistory]
  );

  // ── Commission schedule ───────────────────────────────────────────────────
  const monthsToShow = useMemo(() => {
    const months = [];
    const startParts = commissionStartParts || leaseStartParts;
    if (!startParts || !rentDueDay) return months;
    const nowParts = getTrinidadDateParts(new Date());
    const useLeaseEnd =
      leaseEndParts &&
      (leaseEndParts.year < nowParts.year ||
        (leaseEndParts.year === nowParts.year && leaseEndParts.month < nowParts.month));

    let endY, endM;
    if (useLeaseEnd) {
      endY = leaseEndParts.year;
      endM = leaseEndParts.month;
    } else {
      const throughCurrent = { year: nowParts.year, month: nowParts.month, day: nowParts.day };
      const throughPrepaid = addMonthsToParts(throughCurrent, MONTHS_AHEAD_FOR_PREPAID);
      const capped = leaseEndParts ? minMonthYear(leaseEndParts, throughPrepaid) : throughPrepaid;
      endY = capped.year;
      endM = capped.month;
    }

    let m = startParts.month;
    let y = startParts.year;
    while (y < endY || (y === endY && m <= endM)) {
      months.push({ month: m, year: y });
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return months;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    commissionStartParts,
    leaseEndParts,
    leaseStartParts,
    rentDueDay,
    placementPaidBefore,
    isConverted,
  ]);

  const commissionTotalPages = Math.max(
    1,
    Math.ceil(monthsToShow.length / COMMISSION_MONTHS_PER_PAGE)
  );
  const commissionPageSafe = Math.min(commissionPage, commissionTotalPages);

  const paginatedCommissionMonths = useMemo(() => {
    const start = (commissionPageSafe - 1) * COMMISSION_MONTHS_PER_PAGE;
    return monthsToShow.slice(start, start + COMMISSION_MONTHS_PER_PAGE);
  }, [monthsToShow, commissionPageSafe]);

  useEffect(() => {
    setCommissionPage(1);
  }, [safeProperty._id]);
  useEffect(() => {
    setCommissionPage((p) => Math.min(p, commissionTotalPages));
  }, [commissionTotalPages]);

  // ── Commission totals ─────────────────────────────────────────────────────
  let placementPaid = 0,
    placementPending = 0,
    ongoingPaid = 0,
    ongoingPending = 0;
  for (const { month, year } of monthsToShow) {
    const key = `${year}-${month}`;
    const placement = isPlacementRow(key);
    const expectedAmount = placement ? monthlyRent : monthlyRent * (ongoingRate / 100);
    const payment = paidMap.get(key);
    if (payment) {
      const actualPaid = payment.amount;
      const remaining = Math.max(0, expectedAmount - actualPaid);
      if (placement) {
        placementPaid += actualPaid;
        if (payment.status === "partial") placementPending += remaining;
      } else {
        ongoingPaid += actualPaid;
        if (payment.status === "partial") ongoingPending += remaining;
      }
    } else {
      if (placement) placementPending += expectedAmount;
      else ongoingPending += expectedAmount;
    }
  }
  const totalCommissionToDate = placementPaid + ongoingPaid;
  const firstMonthPaid = placementRowKey
    ? (paidMap.get(placementRowKey)?.status === "paid" || (!paidMap.get(placementRowKey)?.status && paidMap.has(placementRowKey)))
    : false;

  const currentTenant =
    safeProperty.managementInfo?.currentTenant || safeProperty.occupancy?.currentTenant || null;

  // ── Handlers ──────────────────────────────────────────────────────────────
  // rentAlreadyCovered = rent the tenant has already paid for this period (derived from commission collected)
  const rentFromCommission = (commission, isPlacement) =>
    isPlacement
      ? commission
      : Math.round((commission / (ongoingRate / 100)) * 100) / 100;

  const openPayModal = (month, year, amountDue, amountAlreadyPaid = 0, isPlacement = false) => {
    const rentAlreadyCovered = amountAlreadyPaid > 0
      ? rentFromCommission(amountAlreadyPaid, isPlacement)
      : 0;
    const rentRemaining = Math.round((monthlyRent - rentAlreadyCovered) * 100) / 100;
    setPayModal({ open: true, month, year, amountDue, amountAlreadyPaid, isPlacement, rentAlreadyCovered });
    setPayInput(String(Math.max(0, rentRemaining)));
    setPayDate(new Date().toISOString().split("T")[0]);
    setPayNotes("");
    setPayError("");
  };

  const closePayModal = () => {
    if (submittingPay) return;
    setPayModal({ open: false, month: null, year: null, amountDue: 0, amountAlreadyPaid: 0, isPlacement: false, rentAlreadyCovered: 0 });
  };

  const handleSubmitPayment = async () => {
    const rentReceived = parseFloat(payInput);
    const rentRemaining = Math.round((monthlyRent - (payModal.rentAlreadyCovered || 0)) * 100) / 100;
    if (!rentReceived || rentReceived <= 0) {
      setPayError("Please enter a valid rent amount greater than 0");
      return;
    }
    if (rentReceived > rentRemaining + 0.01) {
      setPayError(`Rent received cannot exceed the remaining rent of ${formatCurrency(rentRemaining)}`);
      return;
    }
    // Compute commission earned from the rent received
    const commissionEarned = payModal.isPlacement
      ? Math.round(rentReceived * 100) / 100
      : Math.round(rentReceived * (ongoingRate / 100) * 100) / 100;

    setSubmittingPay(true);
    setPayError("");
    try {
      const res = await fetch(API_ENDPOINTS.properties.markManagementPaid(safeProperty._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          month: payModal.month,
          year: payModal.year,
          amountPaid: commissionEarned,
          ...(payDate ? { paidAt: `${payDate}T12:00:00.000Z` } : {}),
          ...(payNotes.trim() ? { notes: payNotes.trim() } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to record payment");
      onPropertyUpdated?.(data.data);
      await fetchPayments();
      closePayModal();
    } catch (err) {
      setPayError(err.message);
    } finally {
      setSubmittingPay(false);
    }
  };

  const handleSaveRate = async () => {
    const parsed = parseFloat(rateInput);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100 || !safeProperty._id) return;
    setSavingRate(true);
    try {
      const res = await fetch(API_ENDPOINTS.properties.update(safeProperty._id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          pricing: {
            ...safeProperty.pricing,
            rentalCommission: { ...safeProperty.pricing?.rentalCommission, ongoingRate: parsed },
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(data?.message) ? data.message.join(", ") : data?.message;
        throw new Error(msg || "Failed to update rate");
      }
      onPropertyUpdated?.(data.data || data);
      setEditingRate(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingRate(false);
    }
  };

  if (!property) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", fontSize: 14, color: "#6b7280" }}>
        Property data not available
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── KPI strip ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 10,
          marginBottom: "1.25rem",
        }}
      >
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Monthly rent</div>
          <div style={{ ...s.kpiValue, color: "#1E40AF" }}>{formatCurrency(monthlyRent)}</div>
          <div style={s.kpiSub}>Active management</div>
        </div>

        {(!isConverted || !placementPaidBefore) && (
          <div style={s.kpiCard}>
            <div style={s.kpiLabel}>Placement commission</div>
            <div style={{ ...s.kpiValue, color: "#166534" }}>{formatCurrency(monthlyRent)}</div>
            <div style={s.kpiSub}>First month rent</div>
          </div>
        )}

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Ongoing commission</div>
          {editingRate ? (
            <div style={{ marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  style={s.input}
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={rateInput}
                  autoFocus
                  onChange={(e) => setRateInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveRate();
                    if (e.key === "Escape") setEditingRate(false);
                  }}
                />
                <span style={{ fontSize: 13, color: "#6b7280" }}>%</span>
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", margin: "4px 0 8px" }}>
                of monthly rent
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button style={s.btnPrimary} disabled={savingRate} onClick={handleSaveRate}>
                  {savingRate ? (
                    <>
                      <Spinner />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
                <button style={s.btn} disabled={savingRate} onClick={() => setEditingRate(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ ...s.kpiValue, color: "#0F6E56" }}>{ongoingRate}%</div>
              <div style={{ ...s.kpiSub, display: "flex", alignItems: "center", gap: 6 }}>
                of monthly rent
                <button
                  style={s.btnLink}
                  onClick={() => {
                    setRateInput(String(ongoingRate));
                    setEditingRate(true);
                  }}
                >
                  Edit
                </button>
              </div>
            </>
          )}
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total earned</div>
          <div style={s.kpiValue}>{formatCurrency(totalCommissionToDate)}</div>
          <div style={s.kpiSub}>Commission to date</div>
        </div>
      </div>

      {/* ── Commission summary ── */}
      <div style={s.card}>
        <div style={s.cardHead}>Commission summary</div>
        <div style={s.cardBody}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "1rem",
            }}
          >
            <div>
              <div style={s.sectionLabel}>Placement paid</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#166534", marginTop: 4 }}>
                {formatCurrency(placementPaid)}
              </div>
            </div>
            <div>
              <div style={s.sectionLabel}>Placement pending</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#92400E", marginTop: 4 }}>
                {formatCurrency(placementPending)}
              </div>
            </div>
            <div>
              <div style={s.sectionLabel}>Ongoing paid</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1E40AF", marginTop: 4 }}>
                {formatCurrency(ongoingPaid)}
              </div>
            </div>
            <div>
              <div style={s.sectionLabel}>Ongoing pending</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#6b7280", marginTop: 4 }}>
                {formatCurrency(ongoingPending)}
              </div>
            </div>
          </div>
          <div
            style={{
              marginTop: "1rem",
              paddingTop: "0.875rem",
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: 12, color: "#6b7280" }}>Total commission to date</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>
              {formatCurrency(totalCommissionToDate)}
            </div>
          </div>
        </div>
      </div>

      {/* ── Commission structure + management info ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          marginBottom: "1.25rem",
        }}
      >
        {/* Commission structure */}
        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.cardHead}>Commission structure</div>
          <div style={s.cardBody}>
            {(!isConverted || !placementPaidBefore) && (
              <>
                <InfoRow label="Placement" value="First month rent" />
                <InfoRow
                  label="Placement amount"
                  value={<span style={{ color: "#166534" }}>{formatCurrency(monthlyRent)}</span>}
                />
              </>
            )}
            <InfoRow label="Ongoing commission" value={`${ongoingRate}% of monthly rent`} />
            <InfoRow
              label="Paid by"
              value={
                <span style={{ textTransform: "capitalize" }}>
                  {commission.paidBy || "landlord"}
                </span>
              }
            />
            {(!isConverted || !placementPaidBefore) && (
              <InfoRow
                label="Placement status"
                value={
                  firstMonthPaid || commission.isPaid ? (
                    <Pill variant="green">Paid</Pill>
                  ) : (
                    <Pill variant="amber">Pending</Pill>
                  )
                }
              />
            )}
          </div>
        </div>

        {/* Management info */}
        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.cardHead}>Management information</div>
          <div style={s.cardBody}>
            <InfoRow
              label="Status"
              value={
                safeProperty.managementInfo?.isActive !== false ? (
                  <Pill variant="green">Active</Pill>
                ) : (
                  <Pill variant="gray">Inactive</Pill>
                )
              }
            />
            <InfoRow
              label="Contract start"
              value={formatDate(safeProperty.managementInfo?.contractStartDate)}
            />
            <InfoRow
              label="Contract end"
              value={formatDate(safeProperty.managementInfo?.contractEndDate)}
            />
            <InfoRow label="Lease start" value={formatDate(leaseStartRaw)} />
            <InfoRow label="Lease end" value={formatDate(leaseEndRaw)} />
          </div>
        </div>
      </div>

      {/* ── Tenant info ── */}
      {currentTenant?.personalInfo && (
        <div style={s.card}>
          <div style={s.cardHead}>Tenant information</div>
          <div style={s.cardBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "0.875rem",
              }}
            >
              <FieldRow
                label="Name"
                value={`${currentTenant.personalInfo.firstName} ${currentTenant.personalInfo.lastName}`}
              />
              <FieldRow label="Email" value={currentTenant.personalInfo.email || "Not specified"} />
              <FieldRow label="Phone" value={currentTenant.personalInfo.phone || "Not specified"} />
              <FieldRow
                label="Monthly rent"
                value={
                  <span style={{ color: "#166534", fontWeight: 600 }}>
                    {formatCurrency(monthlyRent)}
                  </span>
                }
              />
              <FieldRow label="Lease start" value={formatDate(leaseStartRaw)} />
              <FieldRow label="Lease end" value={formatDate(leaseEndRaw)} />
            </div>
          </div>
        </div>
      )}

      {/* ── Owner info ── */}
      {safeProperty.managementInfo?.ownerInfo && (
        <div style={s.card}>
          <div style={s.cardHead}>Owner information</div>
          <div style={s.cardBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "0.875rem",
              }}
            >
              <FieldRow
                label="Name"
                value={safeProperty.managementInfo.ownerInfo.ownerName || "Not specified"}
              />
              <FieldRow
                label="Contact"
                value={safeProperty.managementInfo.ownerInfo.ownerContact || "Not specified"}
              />
              <FieldRow
                label="Email"
                value={safeProperty.managementInfo.ownerInfo.ownerEmail || "Not specified"}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Commission history (past paid commissions) ── */}
      {commissionHistory.length > 0 && (
        <div style={s.card}>
          <div style={s.cardHead}>
            Commission history
            <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400, marginLeft: 8 }}>
              Past paid commissions
            </span>
          </div>
          <div style={s.cardBody}>
            <div style={{ overflowX: "auto" }}>
              <table className="re-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["Date paid", "Commission type", "Amount", "Paid by"].map((h, i) => (
                      <th key={i} style={{ ...s.th, textAlign: "left" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {commissionHistory.map((row) => (
                    <tr key={row.id}>
                      <td data-label="Date paid" data-stacked="true" style={s.td}>
                        {row.date ? formatDate(row.date) : "—"}
                      </td>
                      <td data-label="Commission type" data-stacked="true" style={{ ...s.td, color: "#6b7280" }}>
                        {row.type}
                        {row.partial && (
                          <span style={{ marginLeft: 6 }}>
                            <Pill variant="amber">Partial</Pill>
                          </span>
                        )}
                      </td>
                      <td data-label="Amount" data-stacked="true" style={{ ...s.td, fontWeight: 600, color: "#166534" }}>
                        {formatCurrency(row.amount)}
                      </td>
                      <td data-label="Paid by" data-stacked="true" style={s.td}>
                        {row.tenant ? (
                          <Link
                            to={`/tenants/${row.tenant._id}`}
                            style={{ color: "#1E40AF", fontSize: 13 }}
                          >
                            {row.paidBy}
                          </Link>
                        ) : (
                          <span style={{ color: "#6b7280" }}>{row.paidBy || "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              style={{
                marginTop: "1rem",
                paddingTop: "0.875rem",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 12, color: "#6b7280" }}>Total paid to date</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#166534" }}>
                {formatCurrency(commissionHistoryTotal)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Commission payments table ── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          Commission payments
          {loadingPayments && (
            <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400 }}>
              <Spinner />
              Loading…
            </span>
          )}
        </div>
        <div style={s.cardBody}>
          {isRenewedTerm && (
            <div
              style={{
                fontSize: 12,
                color: "#3730A3",
                background: "#EEF2FF",
                border: "1px solid #C7D2FE",
                borderRadius: 8,
                padding: "0.625rem 0.75rem",
                marginBottom: "0.875rem",
                lineHeight: 1.5,
              }}
            >
              This lease was renewed — the schedule below starts from the current term.
              Earlier terms' paid commissions are preserved in the{" "}
              <strong>Commission history</strong> section above.
              {unpaidPreviousTermCount > 0 && (
                <>
                  {" "}
                  <span style={{ color: "#92400E", fontWeight: 600 }}>
                    {unpaidPreviousTermCount} unpaid/partial month
                    {unpaidPreviousTermCount === 1 ? "" : "s"} from a previous term
                  </span>{" "}
                  still need reconciling — check Commission history.
                </>
              )}
            </div>
          )}
          {leaseStartParts ? (
            <>
              <p
                style={{
                  fontSize: 12,
                  color: "#6b7280",
                  marginBottom: "0.875rem",
                  lineHeight: 1.6,
                }}
              >
                {isConverted && placementPaidBefore ? (
                  <>
                    The one-time placement commission was already collected as a one-time rental.
                    Ongoing management commission is <strong>{ongoingRate}%</strong>, starting the
                    month after the contract start date.
                  </>
                ) : isConverted && !placementPaidBefore ? (
                  <>
                    The contract-start month uses <strong>full rent</strong> as the placement
                    commission. Each subsequent month is tracked at <strong>{ongoingRate}%</strong>{" "}
                    of rent.
                  </>
                ) : (
                  <>
                    First lease month pays full month rent as placement commission. Subsequent
                    months are tracked at <strong>{ongoingRate}%</strong> of rent.
                  </>
                )}{" "}
                The table includes up to <strong>{MONTHS_AHEAD_FOR_PREPAID} months</strong> beyond
                the current month so you can record prepaid commission.
              </p>

              <div style={{ overflowX: "auto" }}>
                <table className="re-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      {[
                        "Month",
                        "Due date",
                        "Commission type",
                        "Amount",
                        "Status",
                        "Paid by",
                        "",
                      ].map((h, i) => (
                        <th key={i} style={{ ...s.th, textAlign: i === 6 ? "right" : "left" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCommissionMonths.map(({ month, year }) => {
                      const key = `${year}-${month}`;
                      const payment = paidMap.get(key);
                      const isPaid = !payment?.status || payment.status === "paid"
                        ? Boolean(payment)
                        : false;
                      const isPartialRow = payment?.status === "partial";
                      const placement = isPlacementRow(key);
                      const expectedAmount = placement ? monthlyRent : monthlyRent * (ongoingRate / 100);
                      const amountAlreadyPaid = payment?.amount || 0;
                      const balance = Math.round((expectedAmount - amountAlreadyPaid) * 100) / 100;
                      const dayInMonth = Math.min(rentDueDay, new Date(year, month, 0).getDate());

                      return (
                        <tr key={key}>
                          <td
                            data-label="Month"
                            data-stacked="true"
                            style={{
                              ...s.td,
                              borderLeft: placement ? "3px solid #1E40AF" : "3px solid transparent",
                            }}
                          >
                            {monthName(month)} {year}
                          </td>
                          <td data-label="Due date" data-stacked="true" style={s.td}>{formatMonthDayYearTT(year, month, dayInMonth)}</td>
                          <td data-label="Commission type" data-stacked="true" style={{ ...s.td, color: "#6b7280" }}>
                            {placement ? "Placement (1st month)" : `Ongoing (${ongoingRate}%)`}
                          </td>
                          <td data-label="Amount" data-stacked="true" style={{ ...s.td, fontWeight: 600 }}>{formatCurrency(expectedAmount)}</td>
                          <td data-label="Status" data-stacked="true" style={s.td}>
                            {isPaid ? (
                              <Pill variant="green">
                                Paid{payment?.paidAt ? ` · ${formatDate(payment.paidAt)}` : ""}
                              </Pill>
                            ) : isPartialRow ? (
                              <Pill variant="amber">
                                Partial · {formatCurrency(amountAlreadyPaid)} / {formatCurrency(expectedAmount)}
                              </Pill>
                            ) : (
                              <Pill variant="gray">Unpaid</Pill>
                            )}
                          </td>
                          <td data-label="Paid by" data-stacked="true" style={s.td}>
                            {payment?.tenant ? (
                              <Link
                                to={`/tenants/${payment.tenant._id}`}
                                style={{ color: "#1E40AF", fontSize: 13 }}
                              >
                                {payment.tenant.personalInfo.firstName}{" "}
                                {payment.tenant.personalInfo.lastName}
                              </Link>
                            ) : (
                              <span style={{ color: "#9ca3af" }}>Landlord</span>
                            )}
                          </td>
                          <td data-label="" style={{ ...s.td, textAlign: "right" }}>
                            {isPaid ? (
                              <span style={{ color: "#9ca3af" }}>—</span>
                            ) : isPartialRow ? (
                              <button
                                style={s.btnGreen}
                                onClick={() => openPayModal(month, year, expectedAmount, amountAlreadyPaid, placement)}
                              >
                                Add payment · {formatCurrency(balance)} left
                              </button>
                            ) : (
                              <button
                                style={s.btnGreen}
                                onClick={() => openPayModal(month, year, expectedAmount, 0, placement)}
                              >
                                Mark paid
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {monthsToShow.length > COMMISSION_MONTHS_PER_PAGE && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: "1rem",
                    paddingTop: "0.875rem",
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Showing{" "}
                    {monthsToShow.length === 0
                      ? 0
                      : (commissionPageSafe - 1) * COMMISSION_MONTHS_PER_PAGE + 1}
                    –
                    {Math.min(commissionPageSafe * COMMISSION_MONTHS_PER_PAGE, monthsToShow.length)}{" "}
                    of {monthsToShow.length}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button
                      style={s.btn}
                      disabled={commissionPageSafe <= 1}
                      onClick={() => setCommissionPage((p) => Math.max(1, p - 1))}
                    >
                      ← Prev
                    </button>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      {commissionPageSafe} / {commissionTotalPages}
                    </span>
                    <button
                      style={s.btn}
                      disabled={commissionPageSafe >= commissionTotalPages}
                      onClick={() =>
                        setCommissionPage((p) => Math.min(p + 1, commissionTotalPages))
                      }
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 13, color: "#9ca3af" }}>
              Set a lease start date on this management property to enable monthly commission
              tracking.
            </div>
          )}
        </div>
      </div>

      {/* ── Record payment modal ── */}
      {payModal.open && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1060, padding: "1rem",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          }}
          onClick={closePayModal}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 440, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid #e5e7eb", background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#1a1d23" }}>
                {payModal.amountAlreadyPaid > 0 ? "Add payment" : "Record payment"}
              </span>
              <button
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, lineHeight: 1, color: "#6b7280", padding: "0 2px" }}
                onClick={closePayModal}
              >×</button>
            </div>

            {/* Body */}
            <div style={{ padding: "1.25rem", fontSize: 13 }}>
              {/* Month context */}
              <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "9px 13px", marginBottom: "1rem", fontSize: 12, color: "#1E40AF", fontWeight: 500 }}>
                {monthName(payModal.month)} {payModal.year} — commission payment
              </div>

              {/* Info grid */}
              <div className="re-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.1rem" }}>
                <div>
                  <div style={s.fieldLabel}>Monthly rent</div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{formatCurrency(monthlyRent)}</div>
                </div>
                <div>
                  <div style={s.fieldLabel}>Commission type</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                    {payModal.isPlacement ? "Placement (full rent)" : `Ongoing (${ongoingRate}%)`}
                  </div>
                </div>
                {payModal.rentAlreadyCovered > 0 && (
                  <>
                    <div>
                      <div style={s.fieldLabel}>Rent already covered</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#166534" }}>{formatCurrency(payModal.rentAlreadyCovered)}</div>
                    </div>
                    <div>
                      <div style={s.fieldLabel}>Rent remaining</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#92400E" }}>
                        {formatCurrency(Math.round((monthlyRent - payModal.rentAlreadyCovered) * 100) / 100)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Rent received input */}
              <div style={{ marginBottom: "0.875rem" }}>
                <label style={{ ...s.fieldLabel, display: "block", marginBottom: 5 }}>Rent received from tenant</label>
                <div style={{ display: "flex", alignItems: "center", gap: 7, border: "1px solid #d1d5db", borderRadius: 7, padding: "2px 10px", background: "#fff" }}>
                  <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>TTD</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={monthlyRent - (payModal.rentAlreadyCovered || 0)}
                    value={payInput}
                    onChange={(e) => setPayInput(e.target.value)}
                    style={{ flex: 1, fontSize: 15, fontWeight: 700, border: "none", outline: "none", padding: "7px 0", color: "#1a1d23", background: "transparent" }}
                    autoFocus
                  />
                </div>
                {(() => {
                  const rent = parseFloat(payInput);
                  const rentRemaining = Math.round((monthlyRent - (payModal.rentAlreadyCovered || 0)) * 100) / 100;
                  if (!rent || rent <= 0) return null;
                  const commission = payModal.isPlacement
                    ? Math.round(rent * 100) / 100
                    : Math.round(rent * (ongoingRate / 100) * 100) / 100;
                  const isFullRent = rent >= rentRemaining - 0.001;
                  return (
                    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#1E40AF" }}>
                        Commission to record: {formatCurrency(commission)}
                      </div>
                      {isFullRent ? (
                        <div style={{ fontSize: 11, color: "#166534" }}>Full rent received — commission fully settled</div>
                      ) : (
                        <div style={{ fontSize: 11, color: "#92400E" }}>
                          Partial — {formatCurrency(Math.round((rentRemaining - rent) * 100) / 100)} rent still outstanding
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Date paid */}
              <div style={{ marginBottom: "0.875rem" }}>
                <label style={{ ...s.fieldLabel, display: "block", marginBottom: 5 }}>Date received</label>
                <input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  style={{ fontSize: 13, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6, outline: "none", width: "100%", boxSizing: "border-box" }}
                />
              </div>

              {/* Notes */}
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ ...s.fieldLabel, display: "block", marginBottom: 5 }}>
                  Notes <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
                </label>
                <textarea
                  rows={2}
                  maxLength={500}
                  value={payNotes}
                  onChange={(e) => setPayNotes(e.target.value)}
                  placeholder="e.g. Received via bank transfer"
                  style={{ width: "100%", fontSize: 13, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                />
              </div>

              {payError && (
                <div style={{ fontSize: 12, color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: "8px 12px", marginBottom: "0.75rem" }}>
                  {payError}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: "0.875rem 1.25rem", borderTop: "1px solid #e5e7eb", background: "#fafafa", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button style={s.btn} onClick={closePayModal} disabled={submittingPay}>Cancel</button>
              <button style={s.btnPrimary} onClick={handleSubmitPayment} disabled={submittingPay}>
                {submittingPay ? <><Spinner /> Saving…</> : "Save payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── One-time rental placement history ── */}
      {placementPayments.length > 0 && (
        <div style={s.card}>
          <div style={s.cardHead}>One-time rental placement history</div>
          <div style={s.cardBody}>
            <p
              style={{ fontSize: 12, color: "#6b7280", marginBottom: "0.875rem", lineHeight: 1.6 }}
            >
              These are rent payments recorded while this property was a one-time rental, before it
              was converted to property management.
            </p>
            <div style={{ overflowX: "auto" }}>
              <table className="re-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["Month", "Amount", "Status"].map((h, i) => (
                      <th key={i} style={s.th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {placementPayments.map((p) => (
                    <tr key={p._id}>
                      <td data-label="Month" data-stacked="true" style={s.td}>
                        {monthName(p.month)} {p.year}
                      </td>
                      <td data-label="Amount" data-stacked="true" style={{ ...s.td, fontWeight: 600 }}>{formatCurrency(p.amount)}</td>
                      <td data-label="Status" data-stacked="true" style={s.td}>
                        <Pill variant="green">
                          Paid{p.paidAt ? ` · ${formatDate(p.paidAt)}` : ""}
                        </Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyManagementFinancials;
